// The support-need surfaces (bead hf-e4-events-needs-6r5.2).
//
// Pure components over the view models in `needData.ts`, like the event surfaces
// beside them. Copy from `@herefor/product-language`, components from
// `@herefor/design-system`, and no decision about permission taken here.
//
// ── THE WINDOW SECTION IS THE WHOLE BEAD ────────────────────────────────────────
//
// A need's window is independent of its event's (NED-002, invariant 2), and the bead
// is explicit that this is the most confusable idea in the product for the people
// using it. A correct-but-unremarkable pair of time fields would satisfy the schema
// and fail the family.
//
// So the window gets its own fieldset with its own question — "When do you need
// somebody?" — the appointment time is printed NEXT TO IT as context rather than as
// a default, and the form says out loud what the relationship currently is: a wider
// window is confirmed as usually right, an identical one is gently questioned. None
// of that is validation; both are legal. It is the difference between a form that
// permits the right answer and one that helps somebody find it.
//
// ── AVL-007 SURFACES AS A SENTENCE, NOT A CODE ──────────────────────────────────
//
// An overnight window is refused, never split (plan §11.6). The kernel decides that
// and `WINDOW_REJECTION_MESSAGES` holds the words; this file only looks them up.

import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Select,
  StateChip,
  StatusMessage,
  TextInput,
  type StateChipState,
} from "@herefor/design-system";
import {
  LIFECYCLE_NEED_CANCELLATION_MESSAGES,
  NEED_SURFACE_MESSAGES as COPY,
  STATUS_CONTEXT,
  USER_VISIBLE_STATES,
  WINDOW_REJECTION_MESSAGES,
  type UserVisibleState,
  type WindowRejectionReason,
} from "@herefor/product-language";
import type { HelpMode } from "@herefor/domain-kernel";
import { useId, useState, type ChangeEvent, type ReactNode } from "react";

import { PendingAsk } from "../coverage";
import type { FieldProblem, NeedDetailView, NeedFormValues, WindowRelation } from "./needData";

/** What the server says this person may do. Flags, never a role (AUT-007). */
export interface NeedAffordances {
  readonly canEdit: boolean;
  readonly canCancel: boolean;
  readonly canDeleteDraft: boolean;
  /**
   * May this person ask a named member to take this need? (`request.send`, REQ-001.)
   *
   * ITS OWN FLAG, and that is the whole point of adding it. `hf-e5-availability-ccp.2`
   * shipped the could-help surface with `canAsk={affordances.canEdit}` — a deliberate,
   * documented borrow, because the Direct Request mutation did not exist yet and the
   * control was stubbed anyway. `hf-e6-commitments-coverage-udt.1` made the control
   * real, so the borrow had to end: editing a need and sending questions to the family
   * about it are different privileges (PER-008), and a member trusted to correct a
   * window is not thereby trusted to ask people for their Tuesday.
   *
   * It gates withdrawing an ask too — the person who may ask is the person who may
   * un-ask.
   */
  readonly canSendRequests: boolean;
  /**
   * May they read the people directory? (`people.view`; bead hf-mtk.)
   *
   * Separate from `canSendRequests` because the ask-anyone list needs BOTH: the
   * capability to ask, and the capability to read the names it asks from. Inferring
   * one from the other would mount a `people.view`-gated query for somebody who does
   * not hold it, which throws at the boundary rather than rendering nothing.
   */
  readonly canViewPeople: boolean;
  /**
   * May they read recorded availability? (`availability.view`; bead hf-mtk.)
   *
   * The could-help list is built from it, and its query is gated on it — so a
   * membership without it must be offered no list rather than one that throws.
   */
  readonly canViewAvailability: boolean;
}

export const VIEW_ONLY_NEED_AFFORDANCES: NeedAffordances = {
  canEdit: false,
  canCancel: false,
  canDeleteDraft: false,
  canSendRequests: false,
  canViewPeople: false,
  canViewAvailability: false,
};

const NEED_TYPES = ["visit", "drive", "accompany", "call", "errand", "other"] as const;
const HELP_MODES: readonly HelpMode[] = ["in_person", "driving", "phone_or_online"];
const IMPORTANCES = ["ordinary", "important", "critical"] as const;

/* ================================================================== the form === */

export interface NeedFormProps {
  readonly values: NeedFormValues;
  readonly onChange: (values: NeedFormValues) => void;
  readonly onSubmit: () => void;
  readonly problems: readonly FieldProblem[];
  readonly submitLabel: string;
  readonly heading: string;
  /** The form is also the page heading on the live create/edit routes. */
  readonly headingLevel?: 1 | 2;
  /** The event this need hangs off, for context beside the window. Never a default. */
  readonly eventWindowLabel?: string | null;
  readonly windowRelation?: WindowRelation;
  /** True when saving would make people recheck their commitments (§10.8). */
  readonly material?: boolean;
  readonly busy?: boolean;
}

export function NeedForm({
  values,
  onChange,
  onSubmit,
  problems,
  submitLabel,
  heading,
  headingLevel = 2,
  eventWindowLabel = null,
  windowRelation = "unrelated",
  material = false,
  busy = false,
}: NeedFormProps) {
  const headingId = useId();
  const windowHeadingId = useId();
  const Heading = headingLevel === 1 ? "h1" : "h2";

  const setText =
    (key: keyof NeedFormValues) =>
    (changeEvent: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      onChange({ ...values, [key]: changeEvent.target.value });
    };

  const toggleHelpMode = (mode: HelpMode) => {
    const held = values.requiredHelpModes.includes(mode);
    onChange({
      ...values,
      requiredHelpModes: held
        ? values.requiredHelpModes.filter((candidate) => candidate !== mode)
        : [...values.requiredHelpModes, mode],
    });
  };

  const problemFor = (field: string): string | undefined => {
    const raw = problems.find((problem) => problem.field === field)?.message;
    if (raw === undefined) return undefined;
    // A window rejection travels as the kernel's reason code; the reviewed sentence
    // lives in product-language, so it is looked up rather than composed here.
    return isWindowRejection(raw) ? WINDOW_REJECTION_MESSAGES[raw].body : raw;
  };

  return (
    <form
      className="need-form"
      aria-labelledby={headingId}
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();
        onSubmit();
      }}
    >
      <Heading id={headingId}>{heading}</Heading>

      {material ? (
        <StatusMessage tone="warning">
          <strong>{COPY.materialWarningTitle}</strong> {COPY.materialWarningBody}
        </StatusMessage>
      ) : null}

      <TextInput
        label={COPY.labelLabel}
        hint={COPY.labelHint}
        value={values.label}
        onChange={setText("label")}
        error={problemFor("label")}
        required
      />

      <Select
        label={COPY.needTypeLabel}
        value={values.needType}
        onChange={setText("needType")}
        options={NEED_TYPES.map((type) => ({ value: type, label: COPY.needTypes[type] }))}
      />

      {/* THE WINDOW. Its own fieldset, its own question, and the appointment printed
          beside it as context — see the note at the top of this file. */}
      <fieldset className="need-form__window" aria-labelledby={windowHeadingId}>
        <legend id={windowHeadingId}>{COPY.windowTitle}</legend>
        <p className="need-form__window-hint">{COPY.windowIndependenceHint}</p>

        {eventWindowLabel === null ? null : (
          <p className="need-form__event-context">{COPY.aroundEvent(eventWindowLabel)}</p>
        )}

        <TextInput
          label={COPY.windowStartLabel}
          type="datetime-local"
          value={values.startsAtLocal}
          onChange={setText("startsAtLocal")}
          error={problemFor("startsAt")}
          required
        />
        <TextInput
          label={COPY.windowEndLabel}
          type="datetime-local"
          value={values.endsAtLocal}
          onChange={setText("endsAtLocal")}
          error={problemFor("endsAt")}
          required
        />

        {/* Neither of these is a validation failure: both windows are legal. They
            are here because this is the moment somebody has most likely confused
            "when the help is needed" with "when the appointment is". */}
        {windowRelation === "wider" ? (
          <StatusMessage tone="success">{COPY.windowDiffersFromEvent}</StatusMessage>
        ) : null}
        {windowRelation === "identical" ? (
          <StatusMessage tone="info">{COPY.windowMatchesEvent}</StatusMessage>
        ) : null}
      </fieldset>

      <TextInput
        label={COPY.capacityLabel}
        hint={COPY.capacityHint}
        type="number"
        min={1}
        value={values.requiredCapacity}
        onChange={setText("requiredCapacity")}
        error={problemFor("requiredCapacity")}
        required
      />

      <fieldset className="need-form__modes">
        <legend>{COPY.helpModesLabel}</legend>
        <p className="need-form__hint">{COPY.helpModesHint}</p>
        {HELP_MODES.map((mode) => (
          <label key={mode} className="need-form__checkbox">
            <input
              type="checkbox"
              checked={values.requiredHelpModes.includes(mode)}
              onChange={() => {
                toggleHelpMode(mode);
              }}
            />
            {COPY.helpModes[mode]}
          </label>
        ))}
        {problemFor("requiredHelpModes") === undefined ? null : (
          <StatusMessage tone="error" live="assertive">
            {problemFor("requiredHelpModes")}
          </StatusMessage>
        )}
      </fieldset>

      <Select
        label={COPY.importanceLabel}
        hint={COPY.importanceHint}
        value={values.importance}
        onChange={setText("importance")}
        options={IMPORTANCES.map((level) => ({
          value: level,
          label: COPY.importanceLevels[level],
        }))}
      />

      <TextInput
        label={COPY.meetingDetailLabel}
        hint={COPY.meetingDetailHint}
        value={values.meetingDetail}
        onChange={setText("meetingDetail")}
        error={problemFor("meetingDetail")}
      />
      <TextInput
        label={COPY.practicalNoteLabel}
        value={values.practicalNote}
        onChange={setText("practicalNote")}
        error={problemFor("practicalNote")}
      />

      {problemFor("form") === undefined ? null : (
        <StatusMessage tone="error" live="assertive">
          {problemFor("form")}
        </StatusMessage>
      )}

      <Button type="submit" variant="primary" disabled={busy}>
        {submitLabel}
      </Button>
    </form>
  );
}

/* ================================================================ the detail === */

/**
 * Coverage, as the server derived it.
 *
 * A COUNT and a status, never a percentage and never per-hour segment math
 * (NED-012, COV-006, invariant 1). Passed in rather than computed, because the
 * kernel owns the definition and a second one in a browser would be the drifting
 * answer COV-002 forbids.
 */
export interface NeedCoverageView {
  readonly status: UserVisibleState;
  readonly confirmedCapacity: number;
  readonly requiredCapacity: number;
  /**
   * Who has been asked and has not answered (REQ-003; bead hf-9pw).
   *
   * NAMES, not a boolean. It was a boolean, fed a hardcoded `false` by `App.tsx` —
   * so the one surface where somebody decides whether to volunteer was the one
   * surface that could not say "Mary has been asked — waiting for reply", while the
   * coverage week and the chase list both could. The data existed the whole time;
   * this shape is what lets it reach the screen.
   */
  readonly awaitingReplyNames: readonly string[];
}

export interface NeedDetailProps {
  readonly need: NeedDetailView;
  readonly coverage: NeedCoverageView;
  readonly affordances: NeedAffordances;
  readonly committedPeopleCount: number;
  readonly onEdit: () => void;
  readonly onPublish: () => void;
  readonly onCancel: () => void;
  readonly onDeleteDraft: () => void;
  readonly onMarkDone: () => void;
  /**
   * Organiser-only supplementary content, rendered beneath the need's own facts.
   *
   * A SLOT, not a query. `hf-e5-availability-ccp.2`'s could-help list belongs on this
   * screen but is a live subscription, and this file is a pure component tree that
   * must stay testable without a server — so the route composes the two and hands the
   * result down. Anything else that wants a home under a need's detail can use the
   * same seam rather than growing this component another dependency.
   */
  readonly supplementary?: ReactNode;
  readonly busy?: boolean;
  /** The need title is the page heading on the live detail route. */
  readonly headingLevel?: 1 | 2;
}

export function NeedDetail({
  need,
  coverage,
  affordances,
  committedPeopleCount,
  onEdit,
  onPublish,
  onCancel,
  onDeleteDraft,
  onMarkDone,
  supplementary,
  busy = false,
  headingLevel = 2,
}: NeedDetailProps) {
  const [confirming, setConfirming] = useState<"publish" | "cancel" | "delete" | "done" | null>(
    null,
  );
  const isDraft = need.state === "draft";
  const isTerminal = need.state === "cancelled" || need.state === "completed";

  return (
    <Card title={need.label} titleLevel={headingLevel}>
      <p className="need-detail__state">
        <StateChip
          label={USER_VISIBLE_STATES[coverage.status].label}
          state={CHIP[coverage.status]}
        />
        {coverage.requiredCapacity > 1 ? (
          // NED-012: the shortfall reads as a count of people, beside the state.
          <span className="need-detail__count">
            {" · "}
            {STATUS_CONTEXT.coverageCount(coverage.confirmedCapacity, coverage.requiredCapacity)}
          </span>
        ) : null}
        {need.isPast ? <StateChip label={STATUS_CONTEXT.past} state="past" /> : null}
      </p>

      {/* REQ-003: a pending ask never makes a need look safer than it is. It is
          secondary context under "Needs someone", never a sixth state.
          THE COVERAGE FEATURE'S OWN COMPONENT, not a copy of it: `PendingAsk` already
          decides when to name the person and when to fall back to the unnamed
          sentence, and a second implementation here would be a second answer to the
          same question — which is the whole failure this bead exists to remove. */}
      <span data-need-detail-pending-ask>
        <PendingAsk coverage={coverage} />
      </span>

      {isDraft ? <StatusMessage tone="info">{COPY.draftExplanation}</StatusMessage> : null}

      <dl className="need-detail__facts">
        {/* The HELP window, and it is the only window on this surface — NED-010
            forbids showing a helper the event's time as though it were theirs. */}
        <dt>{COPY.windowTitle}</dt>
        <dd>{need.windowLabel}</dd>
        <dt>{COPY.capacityLabel}</dt>
        <dd>{COPY.peopleNeeded(need.requiredCapacity)}</dd>
        <dt>{COPY.helpModesLabel}</dt>
        <dd>{need.requiredHelpModes.map((mode) => COPY.helpModes[mode]).join(", ")}</dd>
        <dt>{COPY.importanceLabel}</dt>
        <dd>{COPY.importanceLevels[need.importance]}</dd>
        <Fact label={COPY.meetingDetailLabel} value={need.meetingDetail} />
        <Fact label={COPY.practicalNoteLabel} value={need.practicalNote} />
      </dl>

      {supplementary === undefined ? null : (
        <div className="need-detail__supplementary">{supplementary}</div>
      )}

      <div className="need-detail__actions">
        {affordances.canEdit && !isTerminal ? (
          <Button variant="secondary" onClick={onEdit} disabled={busy}>
            {COPY.edit}
          </Button>
        ) : null}
        {affordances.canEdit && isDraft ? (
          <Button
            variant="primary"
            onClick={() => {
              setConfirming("publish");
            }}
            disabled={busy}
          >
            {COPY.publish}
          </Button>
        ) : null}
        {affordances.canEdit && !isDraft && !isTerminal ? (
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming("done");
            }}
            disabled={busy}
          >
            {COPY.markDone}
          </Button>
        ) : null}
        {affordances.canCancel && !isDraft && !isTerminal ? (
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming("cancel");
            }}
            disabled={busy}
          >
            {COPY.cancelNeed}
          </Button>
        ) : null}
        {affordances.canDeleteDraft && isDraft ? (
          <Button
            variant="danger"
            onClick={() => {
              setConfirming("delete");
            }}
            disabled={busy}
          >
            {COPY.deleteDraft}
          </Button>
        ) : null}
      </div>

      <Confirm
        open={confirming === "publish"}
        title={COPY.publishTitle}
        body={COPY.publishBody}
        confirmLabel={COPY.publish}
        onConfirm={onPublish}
        onClose={() => {
          setConfirming(null);
        }}
      />
      <Confirm
        open={confirming === "cancel"}
        title={COPY.cancelTitle}
        body={COPY.cancelBody}
        extra={LIFECYCLE_NEED_CANCELLATION_MESSAGES.impact(
          need.eventId !== null,
          committedPeopleCount,
        )}
        confirmLabel={COPY.cancelConfirm}
        danger
        onConfirm={onCancel}
        onClose={() => {
          setConfirming(null);
        }}
      />
      <Confirm
        open={confirming === "delete"}
        title={COPY.deleteDraftTitle}
        body={COPY.deleteDraftBody}
        confirmLabel={COPY.deleteDraftConfirm}
        danger
        onConfirm={onDeleteDraft}
        onClose={() => {
          setConfirming(null);
        }}
      />
      <Confirm
        open={confirming === "done"}
        title={COPY.markDoneTitle}
        body={COPY.markDoneBody}
        // NED-013: completing before the window has started is allowed and must leave
        // a trail, so it is named rather than hidden behind the ordinary button.
        extra={need.isPast ? null : COPY.markDoneEarlyWarning}
        confirmLabel={COPY.markDoneConfirm}
        onConfirm={onMarkDone}
        onClose={() => {
          setConfirming(null);
        }}
      />
    </Card>
  );
}

/* ================================================================== the list === */

export interface NeedListProps {
  readonly heading: string;
  readonly needs: readonly {
    readonly need: NeedDetailView;
    readonly coverage: NeedCoverageView;
    readonly href: string;
  }[];
}

export function NeedList({ heading, needs }: NeedListProps) {
  if (needs.length === 0) {
    return <EmptyState title={COPY.noNeedsYet} description={COPY.noNeedsYetBody} />;
  }

  return (
    <section className="need-list">
      <h2>{heading}</h2>
      <ul className="need-list__items">
        {needs.map((row) => (
          <li key={row.need.supportNeedId} className="need-list__item">
            <a href={row.href}>{row.need.label}</a>
            <p className="need-list__window">{row.need.windowLabel}</p>
            <p className="need-list__status">
              <StateChip
                label={USER_VISIBLE_STATES[row.coverage.status].label}
                state={CHIP[row.coverage.status]}
              />
              {row.coverage.requiredCapacity > 1 ? (
                <span>
                  {" · "}
                  {STATUS_CONTEXT.coverageCount(
                    row.coverage.confirmedCapacity,
                    row.coverage.requiredCapacity,
                  )}
                </span>
              ) : null}
              {row.need.isPast ? <StateChip label={STATUS_CONTEXT.past} state="past" /> : null}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ plumbing --- */

function Fact({ label, value }: { readonly label: string; readonly value: string | null }) {
  if (value === null || value === "") return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

interface ConfirmProps {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  readonly extra?: string | null;
  readonly confirmLabel: string;
  readonly danger?: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

/** Every consequential action goes through one of these. None is a mis-tap away. */
function Confirm({
  open,
  title,
  body,
  extra = null,
  confirmLabel,
  danger = false,
  onConfirm,
  onClose,
}: ConfirmProps) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button
            variant={danger ? "danger" : "primary"}
            onClick={() => {
              onClose();
              onConfirm();
            }}
          >
            {confirmLabel}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {COPY.keep}
          </Button>
        </>
      }
    >
      <p>{body}</p>
      {extra === null ? null : <p>{extra}</p>}
    </Dialog>
  );
}

/**
 * User-visible coverage state → the chip's token.
 *
 * Exhaustive, so a new state cannot fall through to a default colour. COV-013: state
 * is carried by text and icon, never by colour alone, which is why the chip takes an
 * identifier and its words separately.
 */
const CHIP: Record<UserVisibleState, StateChipState> = {
  needsSomeone: "needsSomeone",
  covered: "covered",
  pleaseRecheck: "pleaseRecheck",
  done: "done",
  cancelled: "cancelled",
};

function isWindowRejection(value: string): value is WindowRejectionReason {
  return value === "empty_window" || value === "crosses_day_boundary";
}
